import React, { useState, useMemo, useEffect } from "react";
import { Search } from "lucide-react";
import products from "../data/products.json";
import ProductCard from "../components/ProductCard";

const PRIMARY_COLOR = "text-teal-400";
const ACCENT_BG = "bg-teal-600 hover:bg-teal-500";
const CATEGORY_BG = "bg-gray-700 hover:bg-gray-600 transition-colors duration-200";
const TEXT_COLOR = "text-gray-100";

const Shop = () => {
  const [selectedCategory, setSelectedCategory] = useState("all-products");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // --- Debounce search input ---
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // --- Extract categories dynamically from JSON ---
  const categories = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      const slug = p["category-slug"] || p["category_slug"] || "uncategorized";
      const name = p.category_name || "Uncategorized";
      if (!map.has(slug)) map.set(slug, { name, slug });
    });
    return [{ name: "All Products", slug: "all-products" }, ...map.values()];
  }, []);

  // --- Filter products ---
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const catSlug = p["category-slug"] || p["category_slug"] || "uncategorized";
      const matchesCategory = selectedCategory === "all-products" || catSlug === selectedCategory;
      const matchesSearch = (p.name || "").toLowerCase().includes(debouncedSearch.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, debouncedSearch]);

  return (
    <div className="min-h-screen pt-32 bg-gray-900 px-4 sm:px-6 lg:px-8 font-inter">
      <div className="max-w-7xl mx-auto">
        <h1 className={`text-5xl font-extrabold ${PRIMARY_COLOR} mb-12 border-b border-teal-700 pb-3`}>
          Shop All Products
        </h1>

        {/* Search & Categories */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 space-y-4 md:space-y-0">
          <div className="relative w-full md:w-1/3">
            <Search className={`w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 ${TEXT_COLOR}`} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full bg-gray-800 text-white border border-gray-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-all shadow-inner"
            />
          </div>

          <div className="flex flex-wrap gap-2 md:flex-nowrap md:space-x-3 justify-start md:justify-end">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setSelectedCategory(cat.slug)}
                className={`flex items-center px-5 py-2 rounded-xl font-medium text-sm transition-colors whitespace-nowrap border ${
                  selectedCategory === cat.slug
                    ? `${ACCENT_BG} text-white shadow-md border-teal-500`
                    : `${CATEGORY_BG} ${TEXT_COLOR} border-gray-700 hover:border-teal-600`
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <hr className="my-8 border-gray-800" />

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        ) : (
          <p className="col-span-full text-gray-400 text-center py-12 text-lg border border-dashed border-gray-700 rounded-xl">
            Sorry, no products match your filters.
          </p>
        )}
      </div>
    </div>
  );
};

export default Shop;
